import styled from '@emotion/styled'
import React from 'react'
import { usePrevious } from 'react-use'

import CrawledWebsiteItem from './CrawledWebsiteItem'
import WebsiteSelectionStep from './WebsiteSelectionStep'
import Button from 'components/Button'
import ConfirmationModal from 'components/ConfirmationModal'
import FeatureToggle from 'components/FeatureToggle'
import { FeatureExplanation, FeatureItem, FeaturesList } from 'components/FeaturesList'
import { Portal } from 'components/Portal'
import { ClockIcon, EyeIcon, GiftIcon } from 'components/icons'
import { useFeature } from 'hooks/useFeature'
import { useInstallations } from 'hooks/useInstallations'
import usePageVisibility from 'hooks/usePageVisibility'
import { useToast } from 'hooks/useToast'
import { useTracking } from 'hooks/useTracking'
import { HeadingControlPortalContext } from 'layouts/DefaultLayout'
import { configureRecommendations, updateFeature } from 'lib/api'
import { config } from 'lib/config'
import { useAppStore } from 'lib/store'
import { EVENT_NAMES } from 'lib/tracking'

const POLLING_INTERVAL = config.isTest ? 100 : 5000

const WebsitesOuter = styled.div`
	display: flex;
	flex-direction: column;
	height: min-content;
	margin-top: 48px;
	position: relative;
`

const Caption = styled.p`
	${({ theme }) => theme.typography.captionMedium};
	margin-top: 0;
`

const ProductRecommendationsOuter = styled.div`
	position: relative;
`

const ConfirmDisableModal = ({
	onConfirmSave,
	onKeepEditing,
}: {
	onConfirmSave: () => void
	onKeepEditing: () => void
}) => {
	return (
		<ConfirmationModal
			name="ConfirmDisableModal"
			data-testid="ConfirmDisableModal"
			title="Are you sure you want to turn off product recommendations?"
			caption={
				'You will be able to turn them back on later but you will need to wait while we search your website for products again.'
			}
			hideModal={onKeepEditing}
		>
			<Button kind="secondary" type="button" mediumDesktop onClick={onKeepEditing}>
				Keep recommending
			</Button>
			<Button kind="destructive" type="button" mediumDesktop onClick={onConfirmSave}>
				Turn it off
			</Button>
		</ConfirmationModal>
	)
}

function ProductRecommendationsForm() {
	const store = useAppStore()
	const { installations, areInstallationsLoading } = useInstallations()
	const showToast = useToast()
	const track = useTracking()
	const isPageVisible = usePageVisibility()
	const {
		feature: productRecommendations,
		isFeatureLoading: areProductRecommendationsLoading,
		mutateFeature: mutateProductRecommendations,
	} = useFeature('product_recommendations')

	const crawledWebsite = productRecommendations?.properties.website
	const crawlDate = productRecommendations?.properties.crawledAt
	const isPending = Boolean(crawledWebsite && crawlDate === null)
	const foundProductsCount = productRecommendations?.externalProperties.foundProductsCount
	const failedCrawl = Boolean(!foundProductsCount && !isPending && crawledWebsite)

	const previousIsPending = usePrevious(isPending)
	const domains = React.useMemo(() => Array.from(new Set(installations?.map(({ domain }) => domain))), [installations])

	React.useEffect(() => {
		if (!isPending || failedCrawl || !isPageVisible) {
			return
		}

		const interval = setInterval(() => {
			mutateProductRecommendations()
		}, POLLING_INTERVAL)

		return () => clearInterval(interval)
	}, [isPending, mutateProductRecommendations, failedCrawl, isPageVisible])

	React.useEffect(() => {
		if (!productRecommendations) {
			return
		}

		store.dispatch({
			type: 'productRecommendations/set',
			payload: { enabled: productRecommendations.enabled },
		})
	}, [store, productRecommendations])

	React.useEffect(() => {
		if (previousIsPending && typeof foundProductsCount === 'number' && foundProductsCount > 0) {
			showToast('success', 'Recommendations enabled')
		}
	}, [previousIsPending, foundProductsCount, showToast])

	const hideConfirmationModal = () => store.dispatch({ type: 'currentModal/set', payload: null })

	const handleRecommendationsConnect = async (domain: string | null) => {
		if (!domain) {
			return
		}

		try {
			mutateProductRecommendations(
				{
					name: 'product_recommendations',
					properties: { website: domain, crawledAt: null },
					externalProperties: {
						foundProductsCount: 0,
					},
					enabled: true,
				},
				false,
			)
			await updateFeature('product_recommendations', {
				enabled: true,
			})

			await configureRecommendations(domain)
		} catch {
			showToast('error')
			mutateProductRecommendations(
				{
					name: 'product_recommendations',
					properties: { website: null, crawledAt: null },
					externalProperties: {
						foundProductsCount: 0,
					},
					enabled: false,
				},
				false,
			)
		}
	}

	const handleRecommendationsDisconnect = async () => {
		if (failedCrawl) {
			mutateProductRecommendations(
				{
					name: 'product_recommendations',
					properties: {},
					externalProperties: {
						foundProductsCount: 0,
					},
					enabled: false,
				},
				false,
			)
		}

		try {
			hideConfirmationModal()
			await updateFeature('product_recommendations', {
				enabled: false,
				properties: { website: null, crawledAt: null, autoUpdate: false },
			})

			track(EVENT_NAMES.MODAL_CLOSED, { name: 'Turn off product recommendations', action: 'Turn it off' })
			mutateProductRecommendations(
				{
					name: 'product_recommendations',
					properties: { website: null, crawledAt: null },
					externalProperties: {
						foundProductsCount: 0,
					},
					enabled: false,
				},
				false,
			)
		} catch {
			showToast('error')
		}
	}

	const handleKeepEditing = () => {
		hideConfirmationModal()
		track(EVENT_NAMES.MODAL_CLOSED, { name: 'Turn off product recommendations', action: 'Keep recommending' })
	}

	const handleProductRecommendationSwitchChange = async (isEnabled: boolean): Promise<{ isEnabled: boolean }> => {
		if (!productRecommendations?.properties) {
			return { isEnabled: false }
		}

		store.dispatch({ type: 'productRecommendations/set', payload: { enabled: isEnabled } })
		try {
			await updateFeature('product_recommendations', { enabled: isEnabled })
			mutateProductRecommendations(
				{
					name: 'product_recommendations',
					properties: productRecommendations.properties,
					externalProperties: productRecommendations.externalProperties,
					enabled: isEnabled,
				},
				false,
			)
			showToast('success')
			return { isEnabled }
		} catch {
			showToast('error')
			return { isEnabled: !isEnabled }
		}
	}

	const getCurrentCaption = () => {
		if (isPending) {
			return 'Searching for products on'
		}

		if (!crawledWebsite) {
			return 'Set up recommendations for'
		}

		if (failedCrawl) {
			return null
		}

		if (!productRecommendations.enabled && foundProductsCount) {
			return 'Available recommendations'
		}

		return 'Currently recommending'
	}

	if (areProductRecommendationsLoading || areInstallationsLoading) {
		return null
	}

	return (
		<ProductRecommendationsOuter>
			<ConfirmDisableModal onConfirmSave={handleRecommendationsDisconnect} onKeepEditing={handleKeepEditing} />
			<Portal context={HeadingControlPortalContext}>
				{crawledWebsite && foundProductsCount ? (
					<FeatureToggle
						isEnabled={!!productRecommendations?.enabled}
						onChange={handleProductRecommendationSwitchChange}
						ariaName="product recommendations"
						trackingName="product recommendations"
					/>
				) : null}
			</Portal>
			<FeatureExplanation>
				{"Show personalized product recommendations based on customer's browsing history."}
			</FeatureExplanation>
			<FeaturesList>
				<FeatureItem>
					<ClockIcon />
					<span>Appears after browsing 3 product pages.</span>
				</FeatureItem>
				<FeatureItem>
					<GiftIcon />
					<span>Recommends 3 products from your store.</span>
				</FeatureItem>
				<FeatureItem>
					<EyeIcon />
					<span>Shown once per visit.</span>
				</FeatureItem>
			</FeaturesList>
			{domains && domains.length > 0 && (
				<WebsitesOuter>
					<Caption>{getCurrentCaption()}</Caption>
					{crawledWebsite ? (
						<CrawledWebsiteItem
							handleRecommendationsDisconnect={handleRecommendationsDisconnect}
							handleRetryCrawl={handleRecommendationsConnect}
							crawlDate={crawlDate}
							isPending={isPending}
							failedCrawl={failedCrawl}
							foundProductsCount={foundProductsCount}
							crawledWebsite={crawledWebsite}
						/>
					) : (
						<WebsiteSelectionStep handleRecommendationsConnect={handleRecommendationsConnect} domains={domains} />
					)}
				</WebsitesOuter>
			)}
		</ProductRecommendationsOuter>
	)
}

export default ProductRecommendationsForm
